import React, { useRef, useEffect } from 'react';
import * as d3 from 'd3';
import { ChartProps } from './types';
import { getPerformanceLevel, calculateAbsoluteDifference } from './benchmarkThresholds';

export const StripChart: React.FC<ChartProps> = ({ metrics, chartId = 'strip' }) => {
  const chartRef = useRef<SVGSVGElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const getPerformanceColors = (brandValue: number, industryMean: number) => {
    const absoluteDifference = calculateAbsoluteDifference(brandValue, industryMean);
    const perfLevel = getPerformanceLevel(absoluteDifference);

    const colorMaps = {
      'excellent': { color: '#16a34a', bg: '#dcfce7', performance: 'excellent' as const },
      'very-good': { color: '#22c55e', bg: '#ecfccb', performance: 'very-good' as const },
      'good': { color: '#4ade80', bg: '#f0fdf4', performance: 'good' as const },
      'fair': { color: '#f59e0b', bg: '#fef3c7', performance: 'fair' as const },
      'poor': { color: '#f97316', bg: '#fed7aa', performance: 'poor' as const },
      'critical': { color: '#dc2626', bg: '#fecaca', performance: 'critical' as const }
    };

    return colorMaps[perfLevel.performance];
  };

  useEffect(() => {
    if (!chartRef.current || !containerRef.current || metrics.length === 0) return;

    const container = containerRef.current;
    const svg = d3.select(chartRef.current);
    svg.selectAll('*').remove();

    const containerRect = container.getBoundingClientRect();
    const margin = { top: 40, right: 40, bottom: 80, left: 200 };
    const width = containerRect.width - margin.left - margin.right;
    const height = Math.max(400, metrics.length * 35);

    svg.attr('width', containerRect.width).attr('height', height + margin.top + margin.bottom);

    // Create tooltip
    const tooltip = d3.select('body').selectAll(`.strip-tooltip-${chartId}`)
      .data([null])
      .join('div')
      .attr('class', `strip-tooltip strip-tooltip-${chartId}`)
      .style('position', 'absolute')
      .style('visibility', 'hidden')
      .style('z-index', '1000')
      .style('pointer-events', 'none');

    const g = svg.append('g')
      .attr('transform', `translate(${margin.left}, ${margin.top})`);

    // Filter metrics with industry data
    const validMetrics = metrics.filter(d => d.industryMean && d.industryMean > 0);

    if (validMetrics.length === 0) {
      g.append('text')
        .attr('x', width / 2)
        .attr('y', height / 2)
        .attr('text-anchor', 'middle')
        .attr('font-size', '14px')
        .attr('fill', '#6b7280')
        .text('No industry benchmark data available');
      return;
    }

    // Sort metrics by performance difference for better readability
    const sortedMetrics = [...validMetrics].sort((a, b) => {
      const aDiff = ((a.value - (a.industryMean || 0)) / (a.industryMean || 1)) * 100;
      const bDiff = ((b.value - (b.industryMean || 0)) / (b.industryMean || 1)) * 100;
      return bDiff - aDiff; // Sort descending (best performance first)
    });

    // Calculate scales
    const allValues = sortedMetrics.flatMap(d => [d.value, d.industryMean || 0]);
    const minValue = d3.min(allValues) || 0;
    const maxValue = d3.max(allValues) || 100;
    const valueRange = maxValue - minValue;
    const padding = valueRange * 0.1;

    const xScale = d3.scaleLinear()
      .domain([minValue - padding, maxValue + padding])
      .range([0, width]);

    const yScale = d3.scaleBand()
      .domain(sortedMetrics.map(d => d.categoryName))
      .range([0, height])
      .padding(0.3);

    // Add x-axis
    const xAxis = d3.axisBottom(xScale).tickFormat(d => `${(d as number).toFixed(0)}%`);
    g.append('g')
      .attr('transform', `translate(0, ${height})`)
      .call(xAxis)
      .append('text')
      .attr('x', width / 2)
      .attr('y', 40)
      .attr('text-anchor', 'middle')
      .attr('font-size', '12px')
      .attr('font-weight', '600')
      .attr('fill', '#374151')
      .text('Score');

    // Add alternating background bands for better readability
    sortedMetrics.forEach((d, i) => {
      if (i % 2 === 0) {
        g.append('rect')
          .attr('x', 0)
          .attr('y', yScale(d.categoryName) || 0)
          .attr('width', width)
          .attr('height', yScale.bandwidth())
          .attr('fill', '#f8fafc')
          .attr('opacity', 0.5);
      }
    });

    // Draw data for each metric
    sortedMetrics.forEach((d) => {
      const colors = getPerformanceColors(d.value, d.industryMean || 0);
      const y = (yScale(d.categoryName) || 0) + yScale.bandwidth() / 2;
      const industryX = xScale(d.industryMean || 0);
      const brandX = xScale(d.value);

      // Draw connecting line between industry and brand
      g.append('line')
        .attr('x1', industryX)
        .attr('y1', y)
        .attr('x2', brandX)
        .attr('y2', y)
        .attr('stroke', colors.color)
        .attr('stroke-width', 2)
        .attr('opacity', 0.4);

      // Draw industry average dot
      g.append('circle')
        .attr('cx', industryX)
        .attr('cy', y)
        .attr('r', 4)
        .attr('fill', '#94a3b8')
        .attr('stroke', 'white')
        .attr('stroke-width', 2);

      // Draw brand performance dot
      g.append('circle')
        .attr('cx', brandX)
        .attr('cy', y)
        .attr('r', 6)
        .attr('fill', colors.color)
        .attr('stroke', 'white')
        .attr('stroke-width', 2)
        .style('cursor', 'pointer')
        .on('mouseover', function(event) {
          const absoluteDiff = calculateAbsoluteDifference(d.value, d.industryMean || 0);
          const performanceText = colors.performance === 'excellent' ? 'Outstanding Performance' :
                                 colors.performance === 'very-good' ? 'Strong Performance' :
                                 colors.performance === 'good' ? 'Above Average' :
                                 colors.performance === 'fair' ? 'Below Average' :
                                 colors.performance === 'poor' ? 'Needs Improvement' : 'Urgent Attention Required';

          tooltip.html(`
            <div style="
              background: ${colors.bg};
              border: 2px solid ${colors.color};
              border-radius: 12px;
              padding: 16px;
              min-width: 200px;
              box-shadow: 0 8px 32px -8px rgba(0, 0, 0, 0.25);
              font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
            ">
              <div style="font-weight: 700; font-size: 14px; color: #1e293b; margin-bottom: 8px;">
                ${d.categoryName}
              </div>
              <div style="color: #475569; margin-bottom: 8px; font-size: 12px;">
                ${performanceText}
              </div>
              <div style="display: flex; justify-content: space-between; margin-bottom: 4px;">
                <span style="color: #64748b; font-size: 12px;">Performance Gap:</span>
                <span style="font-weight: 600; color: ${colors.color}; font-size: 12px;">
                  ${absoluteDiff >= 0 ? '+' : ''}${absoluteDiff.toFixed(1)}%
                </span>
              </div>
              <div style="display: flex; justify-content: space-between; margin-bottom: 4px;">
                <span style="color: #64748b; font-size: 12px;">Brand Score:</span>
                <span style="font-weight: 600; color: #1e293b; font-size: 12px;">${d.value.toFixed(1)}%</span>
              </div>
              <div style="display: flex; justify-content: space-between;">
                <span style="color: #64748b; font-size: 12px;">Industry Avg:</span>
                <span style="font-weight: 600; color: #64748b; font-size: 12px;">${d.industryMean?.toFixed(1)}%</span>
              </div>
            </div>
          `)
          .style('visibility', 'visible');

          d3.select(this).attr('r', 8);
        })
        .on('mousemove', function(event) {
          tooltip
            .style('left', (event.pageX + 10) + 'px')
            .style('top', (event.pageY - 10) + 'px');
        })
        .on('mouseout', function() {
          tooltip.style('visibility', 'hidden');
          d3.select(this).attr('r', 6);
        });

      // Add metric label
      const label = g.append('text')
        .attr('x', -10)
        .attr('y', y)
        .attr('text-anchor', 'end')
        .attr('dominant-baseline', 'middle')
        .attr('font-size', '11px')
        .attr('font-weight', '500')
        .attr('fill', '#374151')
        .text(d.categoryName.length > 25 ? d.categoryName.substring(0, 25) + '...' : d.categoryName);

      // Add tooltip for truncated labels
      if (d.categoryName.length > 25) {
        label.append('title').text(d.categoryName);
      }

      // Add performance indicator
      const isImproving = d.value > (d.industryMean || 0);
      const gapText = isImproving ?
        `+${(d.value - (d.industryMean || 0)).toFixed(1)}` :
        `${(d.value - (d.industryMean || 0)).toFixed(1)}`;

      g.append('text')
        .attr('x', Math.max(industryX, brandX) + 10)
        .attr('y', y)
        .attr('dominant-baseline', 'middle')
        .attr('font-size', '10px')
        .attr('font-weight', '600')
        .attr('fill', colors.color)
        .text(gapText);
    });

    // Add legend
    const legend = g.append('g')
      .attr('transform', `translate(${width - 150}, 20)`);

    legend.append('text')
      .attr('x', 0)
      .attr('y', 0)
      .attr('font-size', '12px')
      .attr('font-weight', '600')
      .attr('fill', '#374151')
      .text('Legend');

    // Industry average dot
    legend.append('circle')
      .attr('cx', 8)
      .attr('cy', 20)
      .attr('r', 4)
      .attr('fill', '#94a3b8');

    legend.append('text')
      .attr('x', 18)
      .attr('y', 24)
      .attr('font-size', '10px')
      .attr('fill', '#374151')
      .text('Industry Average');

    // Brand performance dot
    legend.append('circle')
      .attr('cx', 8)
      .attr('cy', 40)
      .attr('r', 6)
      .attr('fill', '#22c55e');

    legend.append('text')
      .attr('x', 18)
      .attr('y', 44)
      .attr('font-size', '10px')
      .attr('fill', '#374151')
      .text('Brand Performance');

    // Cleanup function
    return () => {
      d3.select('body').select(`.strip-tooltip-${chartId}`).remove();
    };
  }, [metrics, chartId]);

  return (
    <div ref={containerRef} style={{ width: '100%' }}>
      <svg ref={chartRef}></svg>
    </div>
  );
};